<script setup lang="ts">
import { computed, ref } from 'vue'
import { VButton, VButtonGroup, VTextField } from '@/modules/shared/components'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { format } from 'date-fns'
import { useRoute } from 'vue-router'
import { useCallStore } from '@/modules/investing/stores/call-store'
import { useInvoiceStore } from '@/modules/investing/stores/invoice-store'
import { Invoice } from '@/modules/investing/types'

const { slug } = useRoute().params as { slug: string }

const props = defineProps<{
  invoice: Invoice
  refetch?: () => Promise<void>
}>()

const store = useCallStore()
const invoiceStore = useInvoiceStore()
const isFormOpen = ref(false)
const invoice = computed(() => props.invoice)

const initialState = {
  paid_at: format(new Date(), 'yyyy-MM-dd'),
}
const rules = {
  paid_at: { required },
}
const form = ref({ ...initialState })
const v$ = useVuelidate(rules, form, { $lazy: true })

const isPaid = computed(() => !!invoice.value?.marked_as_paid)

const isUnpaid = computed(() => !isPaid.value)

const refetch = async () => {
  if (props.refetch) await props.refetch()
}
const markAsUnpaid = async () => {
  const confirm = window.confirm('Are you sure you want to mark this invoice as unpaid?')
  if (!confirm) return

  await invoiceStore.markMultipleInvoicesAsUnpaid([invoice.value.id])
  await refetch()
}

const openForm = () => {
  isFormOpen.value = true
}

const closeForm = () => {
  isFormOpen.value = false
  form.value = { ...initialState }
}

const markAsPaid = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const payload = {
    invoice_ids: [invoice.value?.id],
    paid_at: form.value.paid_at,
  }
  await invoiceStore.markMultipleInvoicesAsPaid(payload)
  await refetch()

  closeForm()
}
</script>

<template>
  <div>
    <div v-if="isPaid">
      <VButton :click="markAsUnpaid" class="w-full" variant="primary">Mark as Unpaid</VButton>
    </div>
    <div v-else-if="!isFormOpen && isUnpaid">
      <VButton @click="openForm" class="w-full" variant="primary">Mark as Paid</VButton>
    </div>
    <div v-if="isFormOpen && isUnpaid" class="rounded border border-gray-150 p-4">
      <div class="mb-6">Mark Invoice as Paid</div>
      <div class="mb-6 space-y-6">
        <VTextField v-model="form.paid_at" label="Payment Date" property="date" type="date" :v$="v$" />
      </div>
      <VButtonGroup>
        <VButton :click="closeForm">Cancel</VButton>
        <VButton :click="markAsPaid">Save</VButton>
      </VButtonGroup>
    </div>
  </div>
</template>
